// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { useCallback, useEffect, useState } from "react";

import {
  CircleAlert,
  CircleCheck,
  History,
  Info,
  RefreshCw,
} from "lucide-react";

import type {
  ChronicleCalibrationClaimSection,
  ChronicleCalibrationInspection,
  ChronicleEvidenceReference,
  CockpitApi,
} from "./api.js";
import { displayState, pluralize, relativeTime } from "./presentation.js";

function claimSubject(value: string): string {
  return value.replace(/^(?:manifest|path-pattern|repository|source):/u, "");
}

function evidenceLabel(evidence: ChronicleEvidenceReference): string {
  if (evidence.source === null) return evidence.path;
  return `${displayState(evidence.source.recordKind)} ${evidence.source.objectId}`;
}

function learningCopy(
  learning: ChronicleCalibrationInspection["evidenceSummary"]["learning"],
): { readonly body: string; readonly title: string } {
  if (learning.status === "active") {
    return {
      title: "Approved project learning is active",
      body: `${String(learning.activeConventionCount)} ${pluralize(learning.activeConventionCount, "Convention")} and ${String(learning.proposalCount)} ${pluralize(learning.proposalCount, "learning proposal")} are recorded with their authority and evidence.`,
    };
  }
  if (learning.status === "awaiting-maintainer") {
    return {
      title: "Maintainer review is required",
      body: `${String(learning.candidateConventionCount)} candidate ${pluralize(learning.candidateConventionCount, "Convention")} and ${String(learning.proposalCount)} ${pluralize(learning.proposalCount, "learning proposal")} change nothing until the required approval and replay evidence exist.`,
    };
  }
  if (learning.status === "observations-recorded") {
    return {
      title: "Observations exist, but no learning is active",
      body: `${String(learning.observationCount)} evidence-backed ${pluralize(learning.observationCount, "Observation")} remain below the promotion boundary. Frequency alone cannot turn them into project policy.`,
    };
  }
  return {
    title: "No project learning has been promoted yet",
    body: "The Chronicle contains repository evidence and review history, but no Observation, Learning Proposal, or maintainer-approved Convention. This is an honest empty state, not a claim that the project has no patterns.",
  };
}

function EvidenceList({
  evidence,
}: {
  readonly evidence: readonly ChronicleEvidenceReference[];
}) {
  if (evidence.length === 0) return null;
  return (
    <details className="chronicle-evidence">
      <summary>
        {evidence.length} evidence {pluralize(evidence.length, "source")}
      </summary>
      <ul>
        {evidence.map((item) => (
          <li key={item.atomId}>
            <strong>{evidenceLabel(item)}</strong>
            <span>
              {displayState(item.sourceAuthority)} · observed{" "}
              {relativeTime(item.validFrom)}
            </span>
            {item.excerpt === null ? null : <p>{item.excerpt}</p>}
          </li>
        ))}
      </ul>
    </details>
  );
}

function ClaimSection({
  empty,
  section,
  title,
}: {
  readonly empty: string;
  readonly section: ChronicleCalibrationClaimSection;
  readonly title: string;
}) {
  return (
    <section className="panel chronicle-map-panel">
      <div className="panel-head">
        <div>
          <h2>{title}</h2>
          <p>
            {String(section.total)} current {pluralize(section.total, "claim")}
          </p>
        </div>
      </div>
      {section.entries.length === 0 ? (
        <div className="empty-state inline">
          <strong>{empty}</strong>
          <span>The absence stays visible instead of being inferred away.</span>
        </div>
      ) : (
        <ul className="chronicle-claim-list">
          {section.entries.map((entry) => (
            <li key={entry.claimId}>
              <div className="chronicle-entry-head">
                <strong>{claimSubject(entry.subject)}</strong>
                <span
                  className={`state-badge ${entry.authority.kind === "maintainer-approved" ? "tone-active" : entry.status === "candidate" ? "tone-attention" : ""}`}
                >
                  {displayState(entry.status)} ·{" "}
                  {Math.round(entry.confidence * 100)}%
                </span>
              </div>
              <p>
                {displayState(entry.predicate)} <strong>{entry.object}</strong>
              </p>
              <small>
                {displayState(entry.kind)} ·{" "}
                {displayState(entry.authority.kind)}
              </small>
              <EvidenceList evidence={entry.evidence} />
            </li>
          ))}
        </ul>
      )}
      {section.truncated ? (
        <p className="panel-note">
          Showing {String(section.entries.length)} representative claims. The
          canonical Chronicle retains all {String(section.total)}.
        </p>
      ) : null}
    </section>
  );
}

export function Chronicle({ api }: { readonly api: CockpitApi }) {
  const [calibration, setCalibration] =
    useState<ChronicleCalibrationInspection | null>(null);
  const [status, setStatus] = useState("Loading Chronicle evidence…");
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setStatus("Loading Chronicle evidence…");
    setError(null);
    try {
      setCalibration(await api.calibration());
      setStatus("");
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Chronicle loading failed.",
      );
      setStatus("");
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const summary = calibration?.evidenceSummary ?? null;
  const learning = summary === null ? null : learningCopy(summary.learning);

  return (
    <div className="page page-wide">
      <header className="page-head">
        <div>
          <h1>Chronicle Explorer</h1>
          <p>
            What Diffclaw knows about this repository, where each statement came
            from, and what has not earned the status of project learning.
          </p>
        </div>
        <div className="page-head-actions">
          <button
            className="button secondary"
            onClick={() => void reload()}
            type="button"
          >
            <RefreshCw size={16} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      {status.length === 0 ? null : (
        <p aria-live="polite" className="status-line">
          {status}
        </p>
      )}

      {error === null ? null : (
        <div className="callout error" role="alert">
          <CircleAlert size={19} strokeWidth={2.2} aria-hidden="true" />
          <div>
            <strong>Chronicle evidence is unavailable</strong>
            <p>
              {error}
              {summary === null
                ? ""
                : ` The evidence below is the last successful inspection from ${relativeTime(summary.generatedAt)}.`}
            </p>
          </div>
        </div>
      )}

      {summary === null || calibration === null || learning === null ? null : (
        <>
          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Current claims</span>
              <span className="metric-value">
                {summary.inventory.projectClaimCount.toLocaleString()}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Evidence atoms</span>
              <span className="metric-value">
                {summary.inventory.evidenceAtomCount.toLocaleString()}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">History events</span>
              <span className="metric-value">
                {summary.history.total.toLocaleString()}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Approved Conventions</span>
              <span className="metric-value">
                {summary.learning.approvedConventionCount.toLocaleString()}
              </span>
            </div>
          </div>

          <div className="callout info">
            {summary.learning.status === "active" ? (
              <CircleCheck size={19} strokeWidth={2.2} aria-hidden="true" />
            ) : (
              <Info size={19} strokeWidth={2.2} aria-hidden="true" />
            )}
            <div>
              <strong>{learning.title}</strong>
              <p>{learning.body}</p>
            </div>
          </div>

          <section className="section">
            <div className="section-head">
              <div>
                <h2>Current repository map</h2>
                <p>
                  Deterministic and authored Claims from repository revision{" "}
                  {summary.inventory.repositoryRevision ?? "not recorded"}.
                  Every visible item retains confidence, authority, and
                  evidence.
                </p>
              </div>
              <span className="state-badge">
                Canonical revision {String(summary.inventory.canonicalRevision)}
                {" · inspected "}
                <time dateTime={summary.generatedAt}>
                  {relativeTime(summary.generatedAt)}
                </time>
              </span>
            </div>
            <div className="chronicle-map-grid">
              <ClaimSection
                empty="No product claims were discovered"
                section={summary.product}
                title="Product and packages"
              />
              <ClaimSection
                empty="No ownership claims were discovered"
                section={summary.ownership}
                title="Ownership"
              />
              <ClaimSection
                empty="No architecture claims were discovered"
                section={summary.architecture}
                title="Architecture and code"
              />
            </div>
          </section>

          <div className="two-columns chronicle-history-grid">
            <section className="section">
              <div className="section-head">
                <div>
                  <h2>Repository history</h2>
                  <p>
                    Recent source events, ordered by observed time and labeled
                    with their authority.
                  </p>
                </div>
              </div>
              <div className="panel">
                {summary.history.categories.length === 0 ? null : (
                  <div className="chronicle-category-strip">
                    {summary.history.categories.map((category) => (
                      <span className="state-badge" key={category.category}>
                        {displayState(category.category)}{" "}
                        {category.count.toLocaleString()}
                      </span>
                    ))}
                  </div>
                )}
                {summary.history.recent.length === 0 ? (
                  <div className="empty-state inline">
                    <strong>No accessible history has been ingested</strong>
                    <span>
                      Complete or repair historical synchronization from
                      Repositories.
                    </span>
                  </div>
                ) : (
                  <ol className="chronicle-timeline">
                    {summary.history.recent.slice(0, 12).map((event) => (
                      <li key={event.atomId}>
                        <History size={16} aria-hidden="true" />
                        <div>
                          <div className="chronicle-entry-head">
                            <strong>{evidenceLabel(event)}</strong>
                            <time dateTime={event.validFrom}>
                              {relativeTime(event.validFrom)}
                            </time>
                          </div>
                          <small>
                            {displayState(event.category)} ·{" "}
                            {displayState(event.sourceAuthority)}
                          </small>
                          {event.excerpt === null ? null : (
                            <p>{event.excerpt}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </section>

            <section className="section">
              <div className="section-head">
                <div>
                  <h2>Review outcomes</h2>
                  <p>
                    Evidence-backed dispositions. These are history, not
                    automatically learned policy.
                  </p>
                </div>
              </div>
              <div className="panel">
                {summary.reviews.dispositions.length === 0 ? null : (
                  <div className="chronicle-category-strip">
                    {summary.reviews.dispositions.map((disposition) => (
                      <span
                        className="state-badge"
                        key={disposition.disposition}
                      >
                        {displayState(disposition.disposition)}{" "}
                        {disposition.count.toLocaleString()}
                      </span>
                    ))}
                  </div>
                )}
                {summary.reviews.recent.length === 0 ? (
                  <div className="empty-state inline">
                    <strong>No Review Episodes are available</strong>
                    <span>
                      Review threads need resolution evidence before they appear
                      here.
                    </span>
                  </div>
                ) : (
                  <ul className="chronicle-review-list">
                    {summary.reviews.recent.map((episode) => (
                      <li key={episode.episodeId}>
                        <div className="chronicle-entry-head">
                          <strong>{episode.sourceObjectId}</strong>
                          <span className="state-badge">
                            {displayState(episode.disposition)}
                          </span>
                        </div>
                        <p>{displayState(episode.rationale)}</p>
                        <time dateTime={episode.observedAt}>
                          Observed {relativeTime(episode.observedAt)}
                        </time>
                        <EvidenceList evidence={episode.evidence} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>

          <section className="panel">
            <div className="panel-head">
              <div>
                <h2>Learning and calibration state</h2>
                <p>
                  Counts preserve the boundary between evidence, candidate
                  knowledge, and approved Convention authority.
                </p>
              </div>
            </div>
            <dl className="chronicle-definition-grid">
              <div>
                <dt>Observations</dt>
                <dd>{summary.learning.observationCount.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Learning proposals</dt>
                <dd>{summary.learning.proposalCount.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Candidate Conventions</dt>
                <dd>
                  {summary.learning.candidateConventionCount.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt>Active Conventions</dt>
                <dd>
                  {summary.learning.activeConventionCount.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt>Contradictory claims</dt>
                <dd>
                  {calibration.contradictionClaimIds.length.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt>Calibration labels</dt>
                <dd>{calibration.reviewThemes.length.toLocaleString()}</dd>
              </div>
            </dl>
            {calibration.reviewThemes.length === 0 ? null : (
              <div className="panel-note">
                Recorded labels:{" "}
                {calibration.reviewThemes.map(displayState).join(", ")}. Labels
                do not create a Convention.
              </div>
            )}
          </section>

          {calibration.uncertainties.length === 0 ? null : (
            <section className="panel">
              <div className="panel-head">
                <div>
                  <h2>Known limits</h2>
                  <p>
                    Missing or uncertain coverage that must remain visible when
                    this Chronicle informs a review.
                  </p>
                </div>
                <span className="count-badge">
                  {calibration.uncertainties.length}
                </span>
              </div>
              <ul className="chronicle-limit-list">
                {calibration.uncertainties.map((uncertainty) => (
                  <li key={uncertainty}>
                    <CircleAlert size={16} aria-hidden="true" />{" "}
                    {displayState(uncertainty)}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
